import { CacheProvider, ThemeProvider } from "@emotion/react";
import { KeyboardArrowDown } from "@mui/icons-material";
import { alpha, createTheme } from "@mui/material";
import { useSelector } from "react-redux";
import rtlPlugin from "stylis-plugin-rtl";
import { prefixer } from "stylis";
import createCache from "@emotion/cache";
import { arSD, enUS as gridEnUS } from "@mui/x-data-grid/locales";
import { useMemo, useState } from "react";
import { arSA, enUS } from "@mui/material/locale";
import "./jazi.css";
import "dayjs/locale/en";
import "dayjs/locale/ar";
import dayjs from "dayjs";

function AppTheme({ children }) {
  const direction = useSelector((state) => state.themeValues.direction);
  const cacheRtl = createCache({
    key: direction === "rtl" ? "mui-rtl" : "mui",
    stylisPlugins: direction === "rtl" ? [prefixer, rtlPlugin] : [],
  });
  const mainColor = useSelector((state) => state.themeValues.mainColor);
  const mode = useSelector((state) => state.themeValues.originalMode);
  const lighterMainColor = alpha(mainColor, 0.15);
  const lightMainColor = alpha(mainColor, 0.8);
  const inputBorder = mode === "dark" ? "#383E56" : "#eeeeee";
  const inputBorderFocus = mainColor;
  const inputBackground = mode === "dark" ? "#2B3147" : "#f9f9f9cc";
  const JaziTheme = useMemo(() => {
    // Set dayjs locale based on direction (ltr or rtl)
    dayjs.locale(direction === "rtl" ? "ar" : "en"); // Use Arabic for rtl, English for ltr

    return createTheme(
      {
        direction: direction,
        typography: {
          fontFamily:
            direction === "ltr"
              ? `Inter,"Nunito Sans", "Roboto", "Helvetica", "Arial", sans-serif`
              : `Tajawal, Poppins, "Roboto", "Helvetica", "Arial", sans-serif`,
          h4: {
            fontWeight: 500,
          },
        },
        palette: {
          mode: mode,
          text: {
            primary: mode === "dark" ? "#D9E6F4" : "#000000",
            main: mode === "dark" ? "#D9E6F4" : "#000000",
          },
          primary: {
            main: mainColor,
            light: lightMainColor,
            lighter: lighterMainColor,
            containedLink: "#fff",
            contrastText: mode === "dark" ? "#154F90" : "#ffffff",
            dimText: mode === "dark" ? "#968b8a" : "#697586",
            lighterText: createTheme().palette.augmentColor({
              color: { main: mode === "dark" ? "#d1d1d1" : "#2e2e2e" },
              name: "lighterText",
            }),
            success: "#34BD7B",
            warning: "#FECE3D",
            error: "#EF3826",
          },
          whiteblack: {
            main: mode === "dark" ? "#25293D" : "#ffffff",
            prominent: mode === "dark" ? "#1E2337" : "#eeeeee",
            fixed: "#ffffff",
            fixedInactive: "#eeeeee",
            contrastText: mode === "dark" ? "#D9E6F4" : "#505050",
          },
          lightButton: {
            main: lighterMainColor,
            contrastText: mode === "dark" ? "#dfdddb" : "#202224",
            dark: mode === "dark" ? "#8d8f00" : "#8280ff",
            border: mode === "dark" ? "#383E56" : "#d5d5d5",
          },
          componentBackgrounds: {
            main: mode === "dark" ? "#1E2337" : "#ffffff",
            page: mode === "dark" ? "#1E2337" : "#fafafa",
            new: inputBackground,
            disabled: mode === "dark" ? "#282828" : "#f0f0f0",
            hover: mode === "dark" ? "#25293D" : lighterMainColor,
            contrastText: mode === "dark" ? "#ededed" : "#707070",
            border: inputBorder,
            borderSearch: mode === "dark" ? "#383E56" : "#eaeaea",
            borderFocus: inputBorderFocus,
            placeholder: mode === "dark" ? "#D9E6F480" : "#bbbbbb",
            chip: mode === "dark" ? "" : "#00000014",
          },
          cancelText: {
            main: mode === "dark" ? "#909090" : "#7f7f7f",
            deleteIcon: "#EF3826",
            disabled: mode === "dark" ? "#D9E6F480" : "#aaaaaa",
            inactive: mode === "dark" ? "#888888" : "#aaaaaa",
            inactiveFixed: "#888888",
            contrastText: mode === "dark" ? "#ededed" : "#000000",
            border: mode === "dark" ? "#333333" : "#dddddd",
          },
          // ADD THIS
          background: {
            // body background (what you see “behind” everything when scrolling)
            default: mode === "dark" ? "#25293D" : "#f5f5f5",
            // surfaces/cards if you use background.paper
            paper: mode === "dark" ? "#25293D" : "#ffffff",
          },
        },
        shape: {
          borderRadius: 8,
        },
        components: {
          MuiTextField: {
            styleOverrides: {
              root: {
                backgroundColor: inputBackground,
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: inputBorder,
                  },
                  "&:hover fieldset": {
                    borderColor: inputBorderFocus,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: inputBorderFocus,
                  },
                },
              },
            },
          },
          MuiFormHelperText: {
            styleOverrides: {
              root: {
                marginLeft: 0,
              },
            },
          },
          MuiSelect: {
            styleOverrides: {
              root: {
                backgroundColor: inputBackground,
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: inputBorder,
                  },
                  "&:hover fieldset": {
                    borderColor: inputBorderFocus,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: inputBorderFocus,
                  },
                },
                ".MuiSvgIcon-root": {
                  color: mainColor,
                },
              },
            },
            defaultProps: {
              IconComponent: (props) => {
                return (
                  <KeyboardArrowDown
                    {...props}
                    sx={{ fontSize: 24 }}
                  ></KeyboardArrowDown>
                );
              },
            },
          },
          MuiDialog: {
            styleOverrides: {
              paper: {
                // backgroundColor: mode === "dark" ? "#25293D" : "#ffffff",   
                backgroundColor: `${mode === "dark" ? "#1E2337" : "#ffffff"} !important`,
                backgroundImage: "none !important",
                // your modal background
                // color: "#D9E6F4",                  // text color if needed
              },
            },
          },
          MuiDialogContent: {
            styleOverrides: { root: { paddingTop: `${8}px !important` } },
          },
          MuiAutocomplete: {
            defaultProps: {
              popupIcon: (
                <KeyboardArrowDown
                  color="primary"
                  sx={{ fontSize: 24 }}
                ></KeyboardArrowDown>
              ),
            },
          },
          MuiMenu: {
            styleOverrides: {
              paper: {
                backgroundColor: mode === "dark" ? "#25293D" : "#ffffff",        // menu panel background
              },
            },
          },
          MuiMenuItem: {
            styleOverrides: {
              root: {
                backgroundColor: "transparent",
                "&.Mui-selected, &.Mui-selected:hover": {
                  backgroundColor: mode === "dark" ? "#1E2337" : "#ffffff",      // selected menu item
                },
                "&:hover": {
                  backgroundColor: mode === "dark" ? "#2B3147" : lighterMainColor,      // hover bg
                },
                color: mode === "dark" ? "#D9E6F4" : "#000000",                  // menu item text
              },
            },
          },
          // MuiDataGrid: {
          //   styleOverrides: {
          //     root: {
          //       "& .MuiDataGrid-columnHeaders": {
          //         backgroundColor: "red",      // your global header bg
          //         color: "#D9E6F4",                // header text
          //       },
          //     },
          //   },
          // },
        },
      },
      direction === "rtl" ? arSA : enUS,
      direction === "rtl" ? arSD : gridEnUS
    );
  }, [direction, mode, mainColor]);
  return (
    <CacheProvider value={cacheRtl}>
      <ThemeProvider theme={JaziTheme}>{children}</ThemeProvider>
    </CacheProvider>
  );
}

function LogoAndText(props) {
  const mode = useSelector((state) => state.themeValues.originalMode);
  const [loaded, setLoaded] = useState(false);

  const handleImageLoad = () => {
    setLoaded(true);
  };
  //const fontSize = props.iFontSize ? props.iFontSize : 16;
  //const fontWeight = props.iFontWeight ? props.iFontWeight : 700;
  return (
    <>
      <img
        alt="logo"
        src={mode === "dark" ? "/logo_dark.png" : "/logo_light.webp"}
        onLoad={handleImageLoad}
        className={`logo ${loaded ? "reveal" : ""}`}
        style={{ width: 220 }}
      ></img>
      {/*<Typography
        color="primary"
        sx={{ fontSize: fontSize, fontWeight: fontWeight }}
      >
        Rewards Extend
      </Typography>*/}
    </>
  );
}

export { AppTheme, LogoAndText };
